<template>
  <section id="skills" class="section">
    <div class="skills__inner">
      <div class="section-header">
        <p class="section-label reveal" ref="el" :class="{ 'is-visible': isVisible }">Technical Expertise</p>
        <h2 class="section-title reveal reveal--d1" :class="{ 'is-visible': isVisible }">Skills & Technologies</h2>
        <p class="section-desc reveal reveal--d2" :class="{ 'is-visible': isVisible }">
          Battle-tested across cloud platforms, distributed processing frameworks,
          and enterprise environments — the stack I ship production systems with.
        </p>
      </div>

      <!-- Bento grid -->
      <div class="skills__bento" ref="gridEl">
        <div
          v-for="(cat, idx) in categories"
          :key="cat.name"
          :class="['skill-card', 'card', `skill-card--${cat.size}`, 'reveal', `reveal--d${idx + 1}`, { 'is-visible': gridVisible }]"
          :style="{ '--accent-color': cat.color }"
        >
          <div class="skill-card__header">
            <div class="skill-card__icon" :style="{ background: `rgba(${cat.colorRgb}, 0.1)`, border: `1px solid rgba(${cat.colorRgb}, 0.2)` }">
              <span>{{ cat.icon }}</span>
            </div>
            <div>
              <h3 class="skill-card__name">{{ cat.name }}</h3>
              <p class="skill-card__count">{{ cat.skills.length }} technologies</p>
            </div>
          </div>
          <div class="skill-card__tags">
            <span
              v-for="skill in cat.skills"
              :key="skill.name"
              class="skill-tag"
              :style="{
                background: `rgba(${cat.colorRgb}, 0.06)`,
                borderColor: `rgba(${cat.colorRgb}, 0.2)`,
                color: cat.color
              }"
            >
              {{ skill.name }}
              <span v-if="skill.level" class="skill-tag__level" :title="`${skill.level}%`">
                <span class="skill-tag__bar" :style="{ width: skill.level + '%', background: cat.color }"></span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { watch } from 'vue'
import { useReveal } from '../composables/useReveal'

const { el, isVisible } = useReveal()
const { el: gridEl, isVisible: gridVisible } = useReveal(0.05)

const categories = [
  {
    name: 'Data Engineering',
    icon: '🔧',
    size: 'large',
    color: '#00d4ff',
    colorRgb: '0, 212, 255',
    skills: [
      { name: 'Python', level: 80 },
      { name: 'SQL', level: 90 },
      { name: 'Apache Spark', level: 78 },
      { name: 'Apache Kafka', level: 70 },
      { name: 'Apache Airflow', level: 72 },
      { name: 'ETL / ELT', level: 90 },
      { name: 'PySpark', level: 75 },
      { name: 'Pandas', level: 82 },
    ],
  },
  {
    name: 'Cloud Platforms',
    icon: '☁️',
    size: 'large',
    color: '#60a5fa',
    colorRgb: '96, 165, 250',
    skills: [
      { name: 'Azure Data Factory' },
      { name: 'Azure Data Lake' },
      { name: 'Azure Databricks' },
      { name: 'Synapse Analytics' },
      { name: 'AWS S3' },
      { name: 'AWS Glue' },
      { name: 'AWS Athena' },
      { name: 'AWS Lambda' },
      { name: 'AWS EC2' },
      { name: 'AWS Quicksight' },
    ],
  },
  {
    name: 'Databases',
    icon: '🗄️',
    size: 'medium',
    color: '#34d399',
    colorRgb: '52, 211, 153',
    skills: [
      { name: 'SQL Server', level: 90 },
      { name: 'MySQL', level: 85 },
      { name: 'PostgreSQL', level: 82 },
      { name: 'Cloud Databases', level: 70 },
      { name: 'SSIS', level: 70 },
      { name: 'SSAS / SSRS' },
    ],
  },
  {
    name: 'DevOps & Infrastructure',
    icon: '🐳',
    size: 'medium',
    color: '#fb923c',
    colorRgb: '251, 146, 60',
    skills: [
      { name: 'Docker', level: 75 },
      { name: 'Kubernetes', level: 60 },
      { name: 'Linux', level: 60 },
      { name: 'Hadoop', level: 65 },
      { name: 'Git / GitHub' },
      { name: 'Networking', level: 50 },
    ],
  },
  {
    name: 'Visualization & BI',
    icon: '📊',
    size: 'medium',
    color: '#a78bfa',
    colorRgb: '167, 139, 250',
    skills: [
      { name: 'Power BI', level: 75 },
      { name: 'Tableau', level: 65 },
      { name: 'Matplotlib', level: 70 },
      { name: 'AWS Quicksight' },
    ],
  },
]

// Apply 'is-visible' manually on gridEl children when grid becomes visible
watch(gridVisible, (val) => {
  if (val && gridEl.value) {
    gridEl.value.querySelectorAll('.reveal').forEach((el, i) => {
      setTimeout(() => el.classList.add('is-visible'), i * 80)
    })
  }
})
</script>

<style scoped>
.skills__inner {
  max-width: 1200px;
  margin: 0 auto;
}

.skills__bento {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 10px;
}

/* Cards */
@property --border-angle {
  syntax: '<angle>';
  inherits: false;
  initial-value: 0deg;
}
@keyframes border-rotate {
  to { --border-angle: 360deg; }
}

/* Original card — unchanged */
.skill-card {
  padding: 16px;
  position: relative;
  overflow: hidden;
}

/* Original top accent — unchanged */
.skill-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 2px;
  background: linear-gradient(to right, var(--accent-color), transparent);
  opacity: 0.6;
}

/* Moving line: draws only on the 1px border ring, not over content */
.skill-card::after {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px;          /* defines the border ring width */
  border-radius: inherit;
  background: conic-gradient(
    from var(--border-angle),
    transparent   0%,
    transparent  25%,
    var(--accent-color) 42%,
    rgba(255, 255, 255, 0.95) 50%,
    var(--accent-color) 58%,
    transparent  75%,
    transparent 100%
  );
  /* Mask: show only the 1px border zone, never the inner content area */
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: destination-out;
  mask-composite: exclude;
  pointer-events: none;
  animation: border-rotate 4s linear infinite;
}

/* Stagger start positions so cards don't all sync */
.skill-card:nth-child(2)::after { animation-delay: -0.8s; }
.skill-card:nth-child(3)::after { animation-delay: -1.6s; }
.skill-card:nth-child(4)::after { animation-delay: -2.4s; }
.skill-card:nth-child(5)::after { animation-delay: -3.2s; }

.skill-card--large  { grid-column: span 6; }
.skill-card--medium { grid-column: span 4; }

.skill-card__header {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 12px;
}
.skill-card__icon {
  width: 36px; height: 36px;
  border-radius: var(--radius-sm);
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; flex-shrink: 0;
}
.skill-card__name {
  font-size: 15px; font-weight: 700;
  color: var(--color-text);
  letter-spacing: -0.01em;
  margin-bottom: 2px;
}
.skill-card__count {
  font-size: 11px; color: var(--color-text-muted);
  font-family: var(--font-mono);
}

.skill-card__tags { display: flex; flex-wrap: wrap; gap: 6px; }

.skill-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 100px;
  font-size: 11.5px;
  font-weight: 500;
  font-family: var(--font-mono);
  border: 1px solid;
  white-space: nowrap;
  height: 28px;
  transition: var(--transition);
}
.skill-tag:hover { transform: translateY(-1px); filter: brightness(1.2); }

.skill-tag__level {
  display: block;
  width: 28px; height: 3px;
  background: rgba(255,255,255,0.08);
  border-radius: 2px;
  overflow: hidden;
}
.skill-tag__bar {
  display: block;
  height: 100%;
  border-radius: 2px;
  background: linear-gradient(90deg,
    #00d4ff 0%,
    #8b5cf6 40%,
    #00d4ff 80%,
    #8b5cf6 100%
  );
  background-size: 250% 100%;
  animation: living-line 2.5s linear infinite;
}

@keyframes living-line {
  0%   { background-position: 0% 50%; }
  100% { background-position: 250% 50%; }
}

@media (max-width: 1100px) {
  .skill-card--large  { grid-column: span 12; }
  .skill-card--medium { grid-column: span 6; }
}
@media (max-width: 768px) {
  .skill-card--large  { grid-column: span 12; }
  .skill-card--medium { grid-column: span 12; }
  .skill-card { padding: 18px; }
  .skill-card__icon { width: 38px; height: 38px; font-size: 18px; }
  .skill-card__name { font-size: 14px; }
  .skills__bento { gap: 12px; }
}
@media (max-width: 480px) {
  .skill-card { padding: 14px; }
  .skill-card__header { gap: 10px; margin-bottom: 14px; }
  .skill-card__icon { width: 34px; height: 34px; font-size: 16px; }
  .skill-card__tags { gap: 5px; }
  .skill-tag { font-size: 11px; padding: 3px 9px; height: 24px; }
  .skill-tag__level { width: 20px; }
}
</style>
